import { css, useTheme } from '@emotion/react';
import { createPortal } from 'react-dom';

import { shadows } from '@/theme/tokens';

import type { Toast, ToastVariant } from './toast-context';
import { useToast } from './toast-context';

function getVariantColor(variant: ToastVariant, theme: ReturnType<typeof useTheme>) {
  switch (variant) {
    case 'error':
      return { border: theme.colors.error, bg: theme.colors.errorMuted };
    case 'success':
      return { border: theme.colors.success, bg: theme.colors.successMuted };
    case 'info':
      return { border: theme.colors.info, bg: theme.colors.infoMuted };
  }
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const theme = useTheme();
  const colors = getVariantColor(toast.variant, theme);

  return (
    <div
      role="alert"
      css={css`
        background: ${colors.bg};
        border: 1px solid ${theme.colors.border};
        border-left: 4px solid ${colors.border};
        border-radius: 8px;
        padding: 12px 16px;
        display: flex;
        gap: 12px;
        align-items: flex-start;
        box-shadow: ${shadows.elevated};
        max-width: 400px;
        width: 100%;
      `}
    >
      <div
        css={css`
          flex: 1;
          min-width: 0;
        `}
      >
        <div
          css={css`
            font-weight: 600;
            font-size: 14px;
            color: ${theme.colors.text};
            margin-bottom: 2px;
          `}
        >
          {toast.title}
        </div>
        <div
          css={css`
            font-size: 13px;
            color: ${theme.colors.textSecondary};
            line-height: 1.4;
          `}
        >
          {toast.message}
        </div>
      </div>
      <div
        css={css`
          display: flex;
          gap: 8px;
          align-items: center;
          flex-shrink: 0;
        `}
      >
        {toast.isRetryable && toast.onRetry && (
          <button
            type="button"
            onClick={() => {
              toast.onRetry!();
              onDismiss();
            }}
            css={css`
              font-size: 13px;
              font-weight: 600;
              color: ${theme.colors.primary};
              background: none;
              border: none;
              cursor: pointer;
              padding: 2px 8px;
              border-radius: 4px;

              &:hover {
                background: ${theme.colors.surface};
              }
            `}
          >
            Retry
          </button>
        )}
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={onDismiss}
          css={css`
            font-size: 18px;
            color: ${theme.colors.textSecondary};
            background: none;
            border: none;
            cursor: pointer;
            padding: 0 4px;
            line-height: 1;

            &:hover {
              color: ${theme.colors.text};
            }
          `}
        >
          &times;
        </button>
      </div>
    </div>
  );
}

export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return createPortal(
    <div
      aria-live="polite"
      aria-label="Notifications"
      css={css`
        position: fixed;
        top: 24px;
        right: 24px;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
      ))}
    </div>,
    document.body,
  );
}
